import React from "react";
import { FiFileText, FiAlertTriangle, FiCheckCircle, FiClock } from "react-icons/fi";

// Compare date string "DD Mon YYYY" to today
const parseDate = (str) => {
  if (!str || str === "—") return null;
  return new Date(str);
};

const getDocStatus = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return { label: "Unknown", color: "#94a3b8", bg: "#f1f5f9" };
  const today = new Date("2026-02-27");
  const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)  return { label: "Expired",       color: "#ef4444", bg: "#fee2e2" };
  if (diffDays <= 30) return { label: `${diffDays}d left`, color: "#f59e0b", bg: "#fef3c7" };
  if (diffDays <= 90) return { label: `${diffDays}d left`, color: "#f97316", bg: "#ffedd5" };
  return { label: "Valid",          color: "#10b981", bg: "#d1fae5" };
};

const DocCell = ({ value }) => {
  const st = getDocStatus(value);
  return (
    <td className="comp-td">
      <div className="comp-doc-cell">
        <div className="comp-doc-date">{value}</div>
        <span className="comp-doc-badge" style={{ background: st.bg, color: st.color }}>
          {st.label}
        </span>
      </div>
    </td>
  );
};

const CompliancePanel = ({ d }) => {
  // Count docs near expiry or expired
  const allDocs = d.compliance.flatMap(v => [
    v.insurance, v.puc, v.permit, v.fitness, v.regExpiry,
  ]);
  const expiredCount  = allDocs.filter(dt => { const s = getDocStatus(dt); return s.label === "Expired"; }).length;
  const expiringSoon  = allDocs.filter(dt => { const s = getDocStatus(dt); return s.label !== "Expired" && s.label !== "Valid" && s.label !== "Unknown"; }).length;
  const validCount    = allDocs.filter(dt => getDocStatus(dt).label === "Valid").length;

  return (
    <div className="fleet-compliance">

      {/* ── Summary ── */}
      <div className="comp-summary-row">
        <div className="comp-sum-card" style={{ borderColor: "#ef4444" }}>
          <FiAlertTriangle size={20} color="#ef4444" />
          <div className="comp-sum-num" style={{ color: "#ef4444" }}>{expiredCount}</div>
          <div className="comp-sum-lbl">Expired Documents</div>
        </div>
        <div className="comp-sum-card" style={{ borderColor: "#f59e0b" }}>
          <FiClock size={20} color="#f59e0b" />
          <div className="comp-sum-num" style={{ color: "#f59e0b" }}>{expiringSoon}</div>
          <div className="comp-sum-lbl">Expiring Soon</div>
        </div>
        <div className="comp-sum-card" style={{ borderColor: "#10b981" }}>
          <FiCheckCircle size={20} color="#10b981" />
          <div className="comp-sum-num" style={{ color: "#10b981" }}>{validCount}</div>
          <div className="comp-sum-lbl">Valid Documents</div>
        </div>
        <div className="comp-ai-note">
          <FiFileText size={15} color="#667eea" />
          {expiredCount > 0
            ? `⚠ ${expiredCount} documents expired — vehicles may not be legally permitted to operate.`
            : "All critical vehicles compliant. Renew expiring documents before due dates."}
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="comp-legend">
        <span className="comp-legend-item" style={{ background: "#fee2e2", color: "#ef4444" }}>Expired</span>
        <span className="comp-legend-item" style={{ background: "#fef3c7", color: "#f59e0b" }}>≤ 30 days</span>
        <span className="comp-legend-item" style={{ background: "#ffedd5", color: "#f97316" }}>31-90 days</span>
        <span className="comp-legend-item" style={{ background: "#d1fae5", color: "#10b981" }}>Valid</span>
      </div>

      {/* ── Compliance Table ── */}
      <div className="comp-table-wrap">
        <table className="comp-table">
          <thead>
            <tr>
              <th className="comp-th">Vehicle</th>
              <th className="comp-th">Insurance</th>
              <th className="comp-th">PUC</th>
              <th className="comp-th">Permit</th>
              <th className="comp-th">Fitness</th>
              <th className="comp-th">Registration</th>
              <th className="comp-th">Driver License</th>
            </tr>
          </thead>
          <tbody>
            {d.compliance.map(c => (
              <tr key={c.vehicleId} className="comp-tr">
                <td className="comp-td comp-vehicle-td">
                  <div className="comp-veh-name">{c.name}</div>
                  <div className="comp-veh-plate">{c.plate}</div>
                </td>
                <DocCell value={c.insurance} />
                <DocCell value={c.puc}       />
                <DocCell value={c.permit}    />
                <DocCell value={c.fitness}   />
                <DocCell value={c.regExpiry} />
                <td className="comp-td">
                  <div className="comp-driver-license">{c.driverLicense}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompliancePanel;
